import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getQueryResponse,
  clearQuery,
  startStreamingQuery,
  appendStreamingChunk,
  finishStreamingQuery,
} from '../features/bills/billSlice';
import { BounceLoader as Spinner } from 'react-spinners';
import axios from 'axios';
import ThumbsUpIcon from './QueryFuncs/ThumbsUpIcon';
import ThumbsDownIcon from './QueryFuncs/ThumbsDownIcon';
import PrintQueryLog from './QueryFuncs/PrintQueryLog';
import recommendedQueries from './QueryFuncs/RecommendedQueries';

const BillQueryForm = ({ billText, version, billId, keyChanges, session, stateCode, state, isQueryDisabled }) => {
  const { queries, isLoadingQuery: isLoading } = useSelector((state) => state.bill);
  const user = useSelector((state) => state.user);
  const auth = user.searchTerm;
  const [query, setQuery] = useState('');
  const [activeButtons, setActiveButtons] = useState({});
  const [notification, setNotification] = useState('');
  const [selectedModel, setSelectedModel] = useState('lightning'); // Default to lightning

  const dispatch = useDispatch();

  const handleFeedback = async (index, query, response, isPositive) => {
    setActiveButtons((prev) => ({
      ...prev,
      [index]: isPositive ? 'up' : 'down',
    }));

    const feedback = {
      query: `The following is a bill text: ${billText}\n\nQ: ${query}`,
      response: response,
      isPositive: isPositive,
      mode: 'precision',
    };

    try {
      const response = await axios.post('/api/bills/submit-feedback', feedback);
      console.log(response.data.message);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const submitWithModel = async (modelType) => {
    console.log('[BillQueryForm] submitWithModel:', modelType);
    if (notification.length > 0) setNotification('');

    if (query.trim().toLowerCase() === 'clear') {
      dispatch(clearQuery({ auth }));
      setQuery('');
      return;
    }

    if (!query.trim()) {
      setNotification('Please enter a question.');
      return;
    }

    const textToSend = billText.length > 50000 ? keyChanges : billText;

    const urlState = (typeof window !== 'undefined' && window.location && window.location.pathname.split('/')[1]) || '';
    const statePrefix = (stateCode || (typeof state !== 'undefined' ? state : '') || urlState || '').toLowerCase();
    const baseEndpoint = `/api/${statePrefix}/bills/query/base`;
    const lightningEndpoint = `/api/${statePrefix}/bills/query/lightning`;
    if (!statePrefix) {
      console.warn('[BillQueryForm] Missing statePrefix; endpoints may be invalid:', {
        baseEndpoint,
        lightningEndpoint,
      });
    }

    if (modelType === 'base') {
      try {
        dispatch(startStreamingQuery({ query, modelType: 'base' }));
        // Get auth token from localStorage for fetch requests
        const user = JSON.parse(localStorage.getItem('user'));
        const headers = {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        };
        if (user?.token) {
          headers['Authorization'] = user.token;
        }

        const response = await fetch(baseEndpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            auth,
            query,
            billText: textToSend,
            billId,
            session,
            mode: 'precision',
            version,
            // modelType forced by endpoint
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        if (!response.body) {
          throw new Error('Response body is not available for streaming');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        try {
          let done = false;
          while (!done) {
            const { value, done: readerDone } = await reader.read();
            done = readerDone;

            if (value) {
              const chunk = decoder.decode(value, { stream: true });
              if (chunk.trim()) {
                dispatch(appendStreamingChunk({ chunk }));
              }
            }
          }
          dispatch(finishStreamingQuery());
        } catch (streamError) {
          console.error('Error reading stream:', streamError);
          throw streamError;
        } finally {
          reader.releaseLock();
        }
      } catch (err) {
        console.error('Streaming failed, falling back to non-stream:', err);
        dispatch(finishStreamingQuery());
        try {
          await dispatch(
            getQueryResponse({
              auth,
              query,
              billText: textToSend,
              billId,
              session,
              mode: 'precision',
              version,
              modelType,
            })
          ).unwrap();
        } catch (fallbackErr) {
          console.error('Fallback request also failed:', fallbackErr);
          setNotification(`Error: ${fallbackErr.message}`);
        }
      }
    } else {
      // Lightning path
      dispatch(startStreamingQuery({ query, modelType: 'lightning' }));
      try {
        // Get auth token from localStorage for fetch requests
        const user = JSON.parse(localStorage.getItem('user'));
        const headers = { 'Content-Type': 'application/json' };
        if (user?.token) {
          headers['Authorization'] = user.token;
        }
        const res = await fetch(lightningEndpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            auth,
            query,
            billText: textToSend,
            billId,
            session,
            mode: 'precision',
            version,
            // modelType forced by endpoint
          }),
        });
        console.log('[BillQueryForm] Lightning POST to', lightningEndpoint);
        if (!res.ok) {
          const errorText = await res.text();
          console.error('Lightning request failed:', res.status, errorText);
          throw new Error(`Lightning request failed: ${res.status} - ${errorText}`);
        }
        const aiText = await res.json();
        // Append the response
        dispatch(appendStreamingChunk({ chunk: aiText }));
        dispatch(finishStreamingQuery());
      } catch (err) {
        console.error('Lightning request error:', err);
        dispatch(finishStreamingQuery());
        setNotification(`Error: ${err.message}`);
      }
      // Don't clear query here
    }
  };

  const handleSubmit = (e) => {
    // Prevent any implicit form submits; use explicit buttons only
    e.preventDefault();
    return false;
  };

  const handleSelectRecommendedQuery = (queryText) => {
    setQuery(queryText);
  };

  // Function to process and return text with bolded words and numbered list
  const renderResponseWithBold = (response) => {
    // First, split the response into parts based on the pattern **bold** and numbers followed by periods
    const parts = response.split(/(\*\*[^*]+\*\*)|(\d+\.\s)/g); // This regex captures both patterns

    return parts.map((part, index) => {
      if (!part) return null; // Filter out any null or undefined parts that may result from split

      // Check for bold pattern
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>; // Remove ** and wrap in <strong>
      }

      // Check for number followed by a period, indicating a new list item
      if (/\d+\.\s/.test(part)) {
        return (
          <React.Fragment key={index}>
            <br />
            <br />
            {part} {/* Add a line break before the number */}
          </React.Fragment>
        );
      }

      // Return other text as is
      return part;
    });
  };

  return (
    <div className="mb-[20px]">
      <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl py-[15px] px-5">
        <form onSubmit={handleSubmit}>
          {/* Conditional rendering of label text based on isQueryDisabled */}
          <div className="border-b border-[#E5E7EB] mb-5 pb-2.5">
            <div className="flex items-center justify-between">
              <h2 id="Office-Information" className="font-playfair text-[28px] text-darkblue font-medium leading-8">
                {isQueryDisabled ? 'Query Disabled for HB1' : 'Enter your query about the bill:'}
              </h2>
              {!isQueryDisabled && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={`rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 transition-all duration-200 ${
                      selectedModel === 'lightning'
                        ? 'bg-[#3B82F6] text-white'
                        : 'bg-white border-2 border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF]'
                    }`}
                    disabled={isLoading || isQueryDisabled}
                    onClick={() => setSelectedModel('lightning')}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Lightning
                  </button>
                  <button
                    type="button"
                    className={`rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 transition-all duration-200 ${
                      selectedModel === 'base'
                        ? 'bg-[#8B5CF6] text-white'
                        : 'bg-white border-2 border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#F5F3FF]'
                    }`}
                    disabled={isLoading || isQueryDisabled}
                    onClick={() => setSelectedModel('base')}
                    aria-label="Use base model"
                  >
                    {/* CPU chip icon to represent standard/base processing */}
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 3v2M12 3v2M15 3v2M9 19v2M12 19v2M15 19v2M3 9h2M3 12h2M3 15h2M19 9h2M19 12h2M19 15h2" />
                      <rect x="7" y="7" width="10" height="10" rx="2" />
                      <rect x="10" y="10" width="4" height="4" rx="1" />
                    </svg>
                    Base
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 mb-[30px]">
            {recommendedQueries['precision'].map((rq, index) => (
              <button
                type="button"
                className="py-2.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-1.5 w-full max-w-[186px] text-center text-sm font-normal text-[#0000F1] whitespace-nowrap hover:bg-[#E5E7EB] transition-all duration-200"
                key={index}
                onClick={() => handleSelectRecommendedQuery(rq.query)}
              >
                {rq.label}
              </button>
            ))}
          </div>

          <div className="flex gap-4 mb-[20px]">
            <input
              type="text"
              className="text-black text-base font-normal border border-[#E5E7EB] rounded-lg flex-1 h-[46px] p-3.5 bg-white focus:outline-none placeholder:text-black focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isQueryDisabled ? '' : 'Type your query here...'} // Optionally clear the placeholder if disabled
              disabled={isQueryDisabled}
            />
            <button
              type="button"
              className="bg-[#83B2FA] hover:bg-lightblue-hover rounded-lg px-6 py-3 text-white text-base font-medium whitespace-nowrap transition-all duration-200"
              disabled={isLoading || isQueryDisabled}
              onClick={() => submitWithModel(selectedModel)}
            >
              Submit Query
            </button>
          </div>
        </form>

        {isQueryDisabled && <p className="queryDisabledMessage">Query functionality is disabled for this bill.</p>}

        <div className="border border-[#E5E7EB] rounded-xl bg-white w-full p-5" id="queryLog">
          <div className="mb-5 flex items-center justify-between">
            <h2 id="Office-Information" className="font-playfair text-[28px] text-darkblue font-medium leading-8">
              Query Log:
            </h2>
            <div className="flex items-center gap-[15px]">
              <button
                className="py-3 px-12 rounded-lg bg-[#83B2FA] text-base font-normal text-white transition-all duration-300 hover:bg-lightblue-hover"
                onClick={PrintQueryLog}
                disabled={queries.length === 0}
              >
                Print
              </button>
            </div>
          </div>
          {isLoading ? (
            <div className="w-full h-full flex justify-center items-center">
              <Spinner loadingContext={'query'} color="#1DB0FF" size={150} />
            </div>
          ) : (
            <div className="">
              {queries.length === 0 ? (
                <>
                  <p className="noQuery">No queries submitted yet.</p>
                  <div className="notification">{notification}</div>
                </>
              ) : (
                queries &&
                queries
                  .slice()
                  .reverse()
                  .map((entry, index) => (
                    <div className="flex items-start gap-4 mb-[20px]" key={index}>
                      <div className="flex flex-col gap-[25px]">
                        <div className="flex flex-wrap items-center gap-[10px] md:gap-[15px]">
                          <h6 className="font-playfair font-medium text-[22px] leading-[29px] text-black">Query: </h6>
                          <p className="text-lg font-normal leading-[30px] text-black">{entry.query}</p>
                          {entry.modelType && (
                            <span
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold tracking-wide ${
                                entry.modelType === 'lightning'
                                  ? 'bg-[#DBEAFE] text-[#1D4ED8]'
                                  : 'bg-[#EDE9FE] text-[#5B21B6]'
                              }`}
                            >
                              {entry.modelType === 'lightning' ? 'Lightning' : 'Base'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-start gap-[15px]">
                          <h6 className="font-playfair font-medium text-[22px] leading-[29px] text-black">
                            Response:{' '}
                          </h6>
                          <p className="text-lg font-normal leading-[30px] text-black">
                            {renderResponseWithBold(entry.response)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2.5">
                        <button
                          className={`good ${activeButtons[index] === 'up' ? 'active' : ''}`}
                          onClick={() => handleFeedback(index, entry.query, entry.response, true)}
                        >
                          <ThumbsUpIcon />
                        </button>
                        <button
                          className={`bad ${activeButtons[index] === 'down' ? 'active' : ''}`}
                          onClick={() => handleFeedback(index, entry.query, entry.response, false)}
                        >
                          <ThumbsDownIcon />
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillQueryForm;
